import React, { CSSProperties, useState } from 'react';
import { Handle, Position, Node, HandleProps, IsValidConnection, NodeResizer, OnResize } from '@xyflow/react';
import { HidroDXEditor } from '../edit';
import { getContrastColor } from '../../core';
import { Box, IconButton, Popover, MenuItem, Slider, TextField, Tooltip, Typography } from '@mui/material';
import { DeleteForever, Edit, TableRows, ViewColumn } from '@mui/icons-material';
import { ColorPicker } from '../modals';

export interface TableRow {
  height: number;
  title?: string;
  style?: CSSProperties;
}

export interface TableNodeCol {
  width: number;
  rows: TableRow[];
}

export interface TableNodeStyle {
  titleBgColor?: string;
  tableStyle: CSSProperties;
  columns: TableNodeCol[];
}

export type TableNodeData = {
  lock?: boolean;
  markdown?: string;
  handles: HandleProps[];
  style: TableNodeStyle;
  onChange?: (newTable: Node<TableNodeData>) => void;
}

const defaultTableHeight = 600;
const defaultTableWidth = 1200;

export function TableNode(nodeData: Node<TableNodeData>) {
  const handleSize = 10;
  const targetColor = "rgb(2, 41, 53)";
  const sourceColor = "rgb(0, 153, 102)";

  const [editRef, setEditRef] = useState<HTMLButtonElement>();
  const [colorPickerEl, setColorPickerEl] = useState<HTMLButtonElement>();
  const [tableStyle, setTableStyle] = useState<TableNodeData>(nodeData.data);


  const handleOpenColorPicker = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setColorPickerEl(ev.currentTarget);
  };

  // Resize Table
  const handleResize: OnResize = (_, params) => {
    const newStyle = {
      ...tableStyle,
      style: {
        ...tableStyle.style,
        tableStyle: {
          ...tableStyle.style.tableStyle,
          width: params.width,
          height: params.height,
        }
      }
    };

    setTableStyle(newStyle);
    if (tableStyle.onChange) tableStyle.onChange({
      ...nodeData,
      data: newStyle,
    });
  };

  const handleChangeTable = (data: TableNodeStyle) => {
    const newStyle = {
      ...tableStyle,
      style: {
        ...data,
        titleBgColor: tableStyle.style.titleBgColor,
        tableStyle: tableStyle.style.tableStyle,
      },
    };

    setTableStyle(newStyle);
    if (tableStyle.onChange) tableStyle.onChange({
      ...nodeData,
      data: newStyle,
    });
  };

  const validateConnection: IsValidConnection = (conn) => {
    if (conn.source === conn.target) return false;
    return true;
  };

  const handles = tableStyle.handles;
  const text = tableStyle.markdown;
  const titleBgColor = tableStyle.style.titleBgColor;
  const locked = tableStyle.lock ?? false;
  const totalWidth = Number(tableStyle.style.tableStyle.width) ?? defaultTableWidth;
  const totalHeight = Number(tableStyle.style.tableStyle.height) ?? defaultTableHeight;

  const topHandles: HandleProps[] = handles.filter(val => val.position === Position.Top);
  const bottomHandles: HandleProps[] = handles.filter(val => val.position === Position.Bottom);
  const leftHandles: HandleProps[] = handles.filter(val => val.position === Position.Left);
  const rightHandles: HandleProps[] = handles.filter(val => val.position === Position.Right);

  return (
    <>
      {editRef &&
        <Popover
          open={Boolean(editRef)}
          anchorEl={editRef}
          onClose={() => setEditRef(undefined)}
          sx={{ width: "556px" }}
        >
          {/* Seletor de Cor */}
          <MenuItem disableRipple sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Cor de Fundo
            </Typography>
            <IconButton onClick={handleOpenColorPicker}>
              <div style={{
                width: "15px",
                height: "15px",
                border: "1px solid black",
                backgroundColor: titleBgColor,
              }} />
            </IconButton>
          </MenuItem>

          <HidroDXEditor
            startText={text}
            onChange={(text) => {
              const newStyle = {
                ...tableStyle,
                markdown: text,
              };

              setTableStyle(newStyle);

              if (tableStyle.onChange) tableStyle.onChange({
                ...nodeData,
                data: newStyle
              });
            }}
          />
        </Popover>
      }

      {/* Title BG color */}
      {colorPickerEl &&
        <ColorPicker
          open={Boolean(colorPickerEl)}
          element={colorPickerEl}
          initialColor={titleBgColor}
          onClose={() => setColorPickerEl(undefined)}
          onColor={(color) => {
            const newStyle = {
              ...tableStyle,
              style: {
                ...tableStyle.style,
                titleBgColor: color,
              },
            };

            setTableStyle(newStyle);
            if (tableStyle.onChange) tableStyle.onChange({
              ...nodeData,
              data: newStyle,
            });
          }}
        />
      }

      <div>
        {/* Handle Top */}
        {topHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: handleSize,
            height: val.type === 'source' ? handleSize : handleSize / 1.5,
            left: ((idx + 1) * (totalWidth / (topHandles.length + 1)))
          };
          return <Handle
            key={`top-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Handle Bottom */}
        {bottomHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: handleSize,
            height: val.type === 'source' ? handleSize : handleSize / 1.5,
            left: ((idx + 1) * (totalWidth / (bottomHandles.length + 1)))
          };

          return <Handle
            key={`bottom-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Left Handles */}
        {leftHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: val.type === 'source' ? handleSize : handleSize / 1.5,
            height: handleSize,
            top: ((idx + 1) * (totalHeight / (leftHandles.length + 1)))
          };

          return <Handle
            key={`left-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Right Handles */}
        {rightHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: val.type === 'source' ? handleSize : handleSize / 1.5,
            height: handleSize,
            top: ((idx + 1) * (totalHeight / (rightHandles.length + 1)))
          };

          return <Handle
            key={`right-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        <NodeResizer
          color='#000a'
          isVisible={!locked}
          minWidth={5}
          minHeight={5}
          onResize={handleResize}
        />

        <div style={{
          width: totalWidth,
          height: totalHeight,
          display: "flex",
          textAlign: "center",
          overflow: "visible",
          position: "relative",
          flexDirection: "column",
          justifyContent: "center",
        }}>

          {/* Editar o Título */}
          {!locked &&
            <div style={{
              transform: 'scale(0.6)',
              transformOrigin: 'center',
              position: 'absolute',
              top: '-35px',
              right: 0,
              width: "38px",
              height: '35px',
              backgroundColor: "#000",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
              zIndex: 100,
            }}>
              <Tooltip title="Editar Título" >
                <IconButton
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setEditRef(ev.currentTarget);
                  }}
                  size='small'
                  sx={{ color: "#FFF" }}
                >
                  <Edit fontSize='small' />
                </IconButton>
              </Tooltip>
            </div>
          }

          {text && <div style={{ border: "1px solid black", backgroundColor: titleBgColor }}>
            <HidroDXEditor key={`${text.length}-${text.slice(Math.max(text.length - 50, 0), text.length)}`} readOnly startText={text} />
          </div>}
          <Table
            initialTableStyle={tableStyle.style}
            onChange={handleChangeTable}
            block={locked}
          />
        </div>
      </div>

    </>
  );
}

interface TableProps {
  block?: boolean;
  initialTableStyle: TableNodeStyle;
  onChange?: (newTable: TableNodeStyle) => void;
}
const Table: React.FC<TableProps> = ({ block, initialTableStyle, onChange }) => {
  const blocked = block ?? false;

  const [tableStyle, setTableStyle] = useState<TableNodeStyle>(initialTableStyle);
  const [editableCol, setEditableCol] = useState<{
    ref: HTMLButtonElement;
    col: TableNodeCol;
    idx: number;
  }>();
  const [editableRow, setEditableRow] = useState<{
    ref: HTMLButtonElement;
    row: TableRow;
    idx: number;
    jdx: number;
  }>();

  const handleAddCol = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, idx: number) => {
    ev.preventDefault();
    ev.stopPropagation();

    const cols = tableStyle.columns;
    cols.splice(idx + 1, 0, {
      width: 100,
      rows: [],
    });

    const newTableStyle = {
      ...tableStyle,
      columns: cols,
    };

    setTableStyle(newTableStyle);
    if (onChange) onChange(newTableStyle);
  };

  const handleEditCol = (newCol: TableNodeCol) => {
    if (!editableCol) return;

    const newTable = {
      ...tableStyle,
      columns: tableStyle.columns.map((col, i) => {
        if (i === editableCol.idx) return newCol;
        return col;
      })
    };

    setTableStyle(newTable);
    if (onChange) onChange(newTable);
  };

  const handleRemoveCol = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, idx: number) => {
    ev.preventDefault();
    ev.stopPropagation();

    const cols = tableStyle.columns.filter((_, i) => i !== idx);
    const newTableStyle = {
      ...tableStyle,
      columns: cols,
    };

    setTableStyle(newTableStyle);
    if (onChange) onChange(newTableStyle);
  };

  const handleAddRow = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, idxCol: number, idx: number) => {
    ev.preventDefault();
    ev.stopPropagation();

    const cols = tableStyle.columns.map((col, i) => {
      if (i === idxCol) {
        col.rows.splice(idx + 1, 0, {
          height: (idx + 1 === 0) ? 10 : 100,
          title: (idx + 1 === 0) ? "**Título**" : "Texto",
          style: {
            backgroundColor: "#FFF0",
          }
        });
      }
      return col;
    });

    const newTableStyle = {
      ...tableStyle,
      columns: [...cols],
    };

    setTableStyle(newTableStyle);
    if (onChange) onChange(newTableStyle);
  };

  const handleEditRow = (newRow: TableRow) => {
    if (!editableRow) return;

    const newTable = {
      ...tableStyle,
      columns: tableStyle.columns.map((col, i) => {
        if (i === editableRow.idx) {
          col.rows = col.rows.map((row, i) => {
            if (i === editableRow.jdx) {
              return newRow;
            }

            return row;
          });
        }

        return col;
      })
    };

    setTableStyle(newTable);
    if (onChange) onChange(newTable);
  };

  const handleRemoveRow = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>, idxCol: number, idx: number) => {
    ev.preventDefault();
    ev.stopPropagation();

    const cols = tableStyle.columns.map((col, i) => {
      if (i === idxCol) col.rows = col.rows.filter((_, i) => i !== idx);
      return col;
    });

    const newTableStyle = {
      ...tableStyle,
      columns: cols,
    };

    setTableStyle(newTableStyle);
    if (onChange) onChange(newTableStyle);
  };

  return <>
    {editableCol &&
      <Popover
        open={Boolean(editableCol)}
        anchorEl={editableCol.ref}
        onClose={() => setEditableCol(undefined)}
      >
        <EditCol
          initialCol={editableCol.col}
          onEdit={handleEditCol}
        />
      </Popover>
    }

    {editableRow &&
      <Popover
        open={Boolean(editableRow)}
        anchorEl={editableRow.ref}
        onClose={() => setEditableRow(undefined)}
      >
        <EditRow
          initialRow={editableRow.row}
          onEdit={handleEditRow}
        />
      </Popover>
    }

    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "row",
      position: 'relative',
    }}>

      {/* Adicionar Coluna Antes */}
      {!blocked &&
        <div style={{
          transform: 'scale(0.6)',
          transformOrigin: 'center',
          position: 'absolute',
          top: "calc(50% - 17.5px)",
          left: "-19px",
          width: "38px",
          height: '35px',
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "15px",
          zIndex: 100,
        }}>
          <Tooltip title="Nova Coluna" >
            <IconButton onClick={(ev) => handleAddCol(ev, -1)} size='small' sx={{ color: "#FFF" }}>
              <ViewColumn fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      }

      {/* Colunas */}
      {tableStyle.columns.map((column, idx) => {
        return <div key={idx} style={{
          display: "flex",
          flex: column.width,
          flexDirection: "column",
          boxSizing: "border-box",
          position: 'relative',
        }}>
          {/* Remover ou Editar Coluna */}
          {!blocked &&
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: "76px",
              height: '35px',
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
              zIndex: 100,
            }}>
              <Tooltip title="Editar Coluna" >
                <IconButton
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();

                    setEditableCol({ ref: ev.currentTarget, col: column, idx });
                  }}

                  size='small'
                  sx={{
                    color: getContrastColor(column.rows?.[0]?.style?.backgroundColor ?? "#FFF"),
                  }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remover Coluna" >
                <IconButton onClick={(ev) => handleRemoveCol(ev, idx)} size='small' sx={{
                  color: getContrastColor(column.rows?.[0]?.style?.backgroundColor ?? "#FFF"),
                }}>
                  <DeleteForever fontSize='small' />
                </IconButton>
              </Tooltip>
            </div>
          }

          {/* Adicionar Coluna Depois */}
          {!blocked &&
            <div style={{
              transform: 'scale(0.6)',
              transformOrigin: 'center',
              position: 'absolute',
              top: "calc(50% - 17.5px)",
              right: "-19px",
              width: "38px",
              height: '35px',
              backgroundColor: "#000",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
              zIndex: 100,
            }}>
              <Tooltip title="Nova Coluna" >
                <IconButton onClick={(ev) => handleAddCol(ev, idx)} size='small' sx={{ color: "#FFF" }}>
                  <ViewColumn fontSize='small' />
                </IconButton>
              </Tooltip>
            </div>
          }

          {/* Nova Linha Antes*/}
          {!blocked &&
            <div style={{
              transform: 'scale(0.6)',
              transformOrigin: 'center',
              position: 'absolute',
              top: "-17.4px",
              right: "calc(50% - 17.5px)",
              width: "35px",
              height: '35px',
              backgroundColor: "#000",
              border: "1px solid black",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
              zIndex: 100,
            }}>
              <Tooltip title="Nova Linha" >
                <IconButton onClick={(ev) => handleAddRow(ev, idx, -1)} size='small' sx={{ color: "#FFF" }}>
                  <TableRows fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          }

          {/* Linhas */}
          {column.rows.map((row, jdx) => {
            return <div key={`${idx}-${jdx}`} style={{
              ...row.style,
              flex: row.height,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid black",
              position: 'relative',
            }}>

              {/* Nova Linha Depois*/}
              {!blocked &&
                <div style={{
                  transform: 'scale(0.6)',
                  transformOrigin: 'center',
                  position: 'absolute',
                  bottom: "-17.4px",
                  right: "calc(50% - 17.5px)",
                  width: "35px",
                  height: '35px',
                  backgroundColor: "#000",
                  border: "1px solid black",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "15px",
                  zIndex: 100,
                }}>
                  <Tooltip title="Nova Linha" >
                    <IconButton onClick={(ev) => handleAddRow(ev, idx, jdx)} size='small' sx={{ color: "#FFF" }}>
                      <TableRows fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              }

              {/* Editar ou Remover Linha */}
              {!blocked &&
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: "76px",
                  height: '35px',
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "15px",
                  zIndex: 100,
                }}>
                  <Tooltip title="Editar Linha" >
                    <IconButton
                      onClick={(ev) => {
                        ev.preventDefault();
                        ev.stopPropagation();

                        setEditableRow({ ref: ev.currentTarget, row: column.rows[jdx], idx, jdx });
                      }}
                      size='small'
                      sx={{ color: getContrastColor(column.rows[jdx]?.style?.backgroundColor ?? "#FFF") }}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remover Linha" >
                    <IconButton onClick={(ev) => handleRemoveRow(ev, idx, jdx)} size='small' sx={{
                      color: getContrastColor(column.rows[jdx]?.style?.backgroundColor ?? "#FFF"),
                    }}>
                      <DeleteForever fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              }

              {row.title && <HidroDXEditor
                key={row.title}
                readOnly
                startText={row.title}
              />}
            </div>;
          })}
        </div>;
      })}

    </div >
  </>;
};

interface EditColProps {
  initialCol: TableNodeCol;
  onEdit: (col: TableNodeCol) => void;
}
const EditCol: React.FC<EditColProps> = ({ initialCol, onEdit }) => {
  const [col, setCol] = useState(initialCol);

  const handleEditCol = (
    ev: Event | React.MouseEvent<HTMLButtonElement, MouseEvent> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
    col: TableNodeCol,
  ) => {
    ev?.preventDefault();
    ev?.stopPropagation();

    setCol(col);
    onEdit(col);
  };

  return <>
    <div style={{
      width: "456px",
      display: 'flex',
      flexDirection: "column",
    }}>
      {/* Editor de Altura */}
      <MenuItem disableRipple sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">
          Largura
        </Typography>
        <Box sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
          <Slider
            value={col.width}
            onChange={(ev, value) => handleEditCol(ev, {
              ...col,
              width: value as number,
            })}
            min={5}
            max={Math.max((col.width * 1.1), 500)}
            step={1}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            size="small"
            value={col.width}
            onChange={(e) => handleEditCol(e, {
              ...col,
              width: Number(e.target.value),
            })}
            type="number"
            sx={{ width: '150px' }}
          />
        </Box>
      </MenuItem>
    </div>
  </>;
};

interface EditRowProps {
  initialRow: TableRow;
  onEdit: (row: TableRow) => void;
}
const EditRow: React.FC<EditRowProps> = ({ initialRow, onEdit }) => {
  const [row, setRow] = useState(initialRow);
  const [colorPickerEl, setColorPickerEl] = useState<HTMLButtonElement>();

  const handleOpenColorPicker = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setColorPickerEl(ev.currentTarget);
  };

  const handleEditRow = (
    ev: Event | React.MouseEvent<HTMLButtonElement, MouseEvent> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
    row: TableRow,
  ) => {
    ev?.preventDefault();
    ev?.stopPropagation();

    setRow(row);
    onEdit(row);
  };

  return <>
    {colorPickerEl &&
      <ColorPicker
        open={Boolean(colorPickerEl)}
        element={colorPickerEl}
        initialColor={row.style?.backgroundColor}
        onClose={() => setColorPickerEl(undefined)}
        onColor={(color) => handleEditRow(undefined, {
          ...row,
          style: {
            ...row.style,
            backgroundColor: color,
          }
        })}
      />
    }

    <div style={{
      width: "556px",
      height: "456px",
      display: 'flex',


      flexDirection: "column",
    }}>
      {/* Editor de Altura */}
      <MenuItem disableRipple sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">
          Altura
        </Typography>
        <Box sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
          <Slider
            value={row.height}
            onChange={(ev, value) => handleEditRow(ev, {
              ...row,
              height: value as number,
            })}
            min={5}
            max={Math.max((row.height * 1.1), 500)}
            step={1}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            size="small"
            value={row.height}
            onChange={(e) => handleEditRow(e, {
              ...row,
              height: Number(e.target.value),
            })}
            type="number"
            sx={{ width: '150px' }}
          />
        </Box>
      </MenuItem>

      {/* Seletor de Cor */}
      <MenuItem disableRipple sx={{ flexDirection: 'row', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Cor de Fundo
        </Typography>
        <IconButton onClick={handleOpenColorPicker}>
          <div style={{
            width: "15px",
            height: "15px",
            border: "1px solid black",
            backgroundColor: row.style?.backgroundColor,
          }} />
        </IconButton>
      </MenuItem>

      {/* Texto */}
      <HidroDXEditor
        startText={row.title}
        onChange={(text) => handleEditRow(undefined, {
          ...row,
          title: text,
        })}
      />
    </div>
  </>;
};
